// MODIFIED: Added getLoanDetailsAndSchedule to hook destructuring and modal props
import React, { useState } from "react";
import { Button, SearchBar, GeneralMessage } from "../../../../common/APICommon";
import { LoadingSpinner } from "../../../../common/Loading";
import { ConfirmationModal } from "../../../../common/Modal";
import { EmployeePickerModal } from "../../../../common/selectComponents/EmployeePicker";
import NoteAddIcon from "@mui/icons-material/NoteAdd";

import { useSalaryAdvanceManagement } from "../../../hooks/useSalaryAdvanceManagement";

import { SalaryAdvanceTable } from "./components/SalaryAdvanceTable";
import { SalaryAdvanceSubmitModal } from "./components/SalaryAdvanceSubmitModal";
import { SalaryAdvanceLoanDetailModal } from "./components/SalaryAdvanceLoanDetailModal";
import { SalaryAdvanceHistoryModal } from "./components/SalaryAdvanceHistoryModal";
import { SalaryAdvanceViewModal } from "./components/SalaryAdvanceViewModal";
import { useSalaryAdvanceDeductionBracket } from "../../../hooks/useSalaryAdvanceDeductionBracket";

function SalaryAdvanceModule({ user, userAccess }) {
  const {
    loading: sarLoading,
    setLoading: sarSetLoading,
    showMessage: sarShowMessage,
    message: sarMessage,
    isSuccess: sarIsSuccess,
    data,
    getLoanDetailsAndSchedule,
    hasActiveSalaryAdvanceLoan,
    previewSchedule,
    submitRequest,
    cancelRequest,
    createLoan,
    updateLoan,
    activateLoan,
    releaseLoan,
  } = useSalaryAdvanceManagement(user, userAccess);

  const {
    loading: sadbLoading,
    showMessage: sadbShowMessage,
    message: sadbMessage,
    isSuccess: sadbIsSuccess,
    getDeductionAmountByCutoffCount
  } = useSalaryAdvanceDeductionBracket();


  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modals, setModals] = useState({
    emp: false,
    submit: false,
    loan: false,
    history: false,
    cancel: false,
    activate: false,
    view: false
  });
  const [selected, setSelected] = useState({ emp: null, sar: null });

  const toggle = (m, v) => setModals(p => ({ ...p, [m]: v }));

  const isFullLoanAccessAllowed = userAccess?.isFullLoanAccess;
  const isAssignmentManager = userAccess?.isSalaryAdvanceAssignmentManager;

  const handleEmployeeSelect = async (employee) => {
    try {
      sarSetLoading(true);

      // check first if the employee has ongoing loan, processing request, ongoing request, for approval request
      const hasOngoingLoan = await hasActiveSalaryAdvanceLoan(employee?.id);

      if (hasOngoingLoan) return; // the message is initiated inside the hasActiveSalaryAdvanceLoan

      setSelected(p => (
        {
          ...p, emp:
          {
            employeePersonalInformationId: employee.id,
            employeeFullName: employee.fullName
          }
        }));
      toggle("emp", false);
      toggle("submit", true);
    } catch (error) {
      console.error(": ", error)
    } finally {
      sarSetLoading(false);
    }
  }

  const handleSetLoanDetailClick = async (sar) => {
    // remove the old way of getting deduction amount
    const sarWithBracket = {
      ...sar,
    };

    setSelected(p => ({ ...p, sar: sarWithBracket }));
    toggle("loan", true);
  }

  return (
    <div className="transaction-body">
      <div className="flex flex-row justify-between mb-4">
        <SearchBar handleFilter={setSearchTerm} parentClassName="m-0" />
        {isFullLoanAccessAllowed && isAssignmentManager && (
          <Button
            icon={<NoteAddIcon />}
            name="ADD"
            className="bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"
            onClick={() => toggle("emp", true)}
          />
        )}
      </div>

      <div className="transaction-table container">
        <SalaryAdvanceTable
          sarList={data}
          userAccess={userAccess}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          onView={(sar) => { setSelected(p => ({ ...p, sar })); toggle("view", true); }}
          onSetLoanDetail={(sar) => { handleSetLoanDetailClick(sar) }}
          onActivate={(sar) => { setSelected(p => ({ ...p, sar })); toggle("activate", true); }}
          onRelease={(sar) => { setSelected(p => ({ ...p, sar })); toggle("release", true); }}
          onCancel={(sar) => { setSelected(p => ({ ...p, sar })); toggle("cancel", true); }}
          onHistory={(sar) => { setSelected(p => ({ ...p, sar })); toggle("history", true); }}
        />
      </div>

      <EmployeePickerModal
        isOpen={modals.emp}
        onClose={() => toggle("emp", false)}
        setData={(employee) => handleEmployeeSelect(employee)}
      />

      <SalaryAdvanceSubmitModal
        isOpen={modals.submit}
        onClose={() => toggle("submit", false)}
        selectedEmployee={selected.emp}
        onSubmit={submitRequest}
      />

      <SalaryAdvanceViewModal
        isOpen={modals.view}
        onClose={() => toggle("view", false)}
        selectedSar={selected.sar}
      />

      {/* MODIFIED: Ensure getLoanDetails prop is passed here */}
      <SalaryAdvanceLoanDetailModal
        isOpen={modals.loan}
        onClose={() => toggle("loan", false)}
        selectedSar={selected.sar}
        onSave={createLoan}
        onUpdate={updateLoan}
        getLoanDetails={getLoanDetailsAndSchedule}
        getDeductionAmountByCutoffCount={getDeductionAmountByCutoffCount}
        previewSchedule={previewSchedule}
      />

      {/* MODIFIED: Ensure getHistory prop is passed here */}
      <SalaryAdvanceHistoryModal
        isOpen={modals.history}
        onClose={() => toggle("history", false)}
        selectedSar={selected.sar}
        getHistory={getLoanDetailsAndSchedule}
      />

      <ConfirmationModal
        visible={modals.cancel}
        onClose={() => toggle("cancel", false)}
        onConfirm={() => cancelRequest(selected.sar?.id).then(() => toggle("cancel", false))}
        title="Cancel Request"
        message={`Are you sure you want to cancel ${selected.sar?.referenceNumber}?`}
        confirmText="Yes"
        type="warning"
      />

      <ConfirmationModal
        visible={modals.activate}
        onClose={() => toggle("activate", false)}
        onConfirm={() => activateLoan(selected.sar?.loanId).then(() => toggle("activate", false))}
        title="Activate Loan"
        message={`Start this loan and lock terms for ${selected.sar?.referenceNumber}?`}
        confirmText="Yes"
        type="info"
      />

      <ConfirmationModal
        visible={modals.release}
        onClose={() => toggle("release", false)}
        onConfirm={() => releaseLoan(selected.sar?.loanId).then(() => toggle("release", false))}
        title="Release Loan"
        message={`Release this loan to ${selected.sar?.employeeFullName || 'this employee'}?`}
        confirmText="Yes"
        type="info"
      />

      <LoadingSpinner loading={sarLoading || sadbLoading} />
      <GeneralMessage
        form={sarShowMessage}
        message={sarMessage}
        isSuccess={sarIsSuccess}
      />
      <GeneralMessage
        form={sadbShowMessage}
        message={sadbMessage}
        isSuccess={sadbIsSuccess}
      />
    </div>
  );
}

export default SalaryAdvanceModule;